import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Statistic, Typography, Select, Alert, Spin, Tag, Progress } from 'antd';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  BarChartOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import { getProjects, getProjectDashboard } from '../services/api';

const { Title, Text } = Typography;

interface Project {
  id: string;
  code: string;
  name: string;
  client_name: string;
  criticality: string;
  status: string;
}

const ClientDashboardPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [projectDetails, setProjectDetails] = useState<Project | null>(null);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].id);
        setProjectDetails(response.data[0]);
        loadDashboard(response.data[0].id);
      }
    } catch (err) {
      setError('Failed to load projects.');
    }
  };

  const loadDashboard = async (projectId: string) => {
    setLoading(true);
    try {
      const response = await getProjectDashboard(projectId);
      setData(response.data);
    } catch (err) {
      setError('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (value: string) => {
    setSelectedProject(value);
    const proj = projects.find(p => p.id === value);
    setProjectDetails(proj || null);
    loadDashboard(value);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1B2A4A 0%, #2E75B6 100%)',
        borderRadius: 12,
        padding: '24px',
        marginBottom: 24,
        color: 'white',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={3} style={{ color: 'white', margin: 0 }}>
              Client Project Dashboard
            </Title>
            <Text style={{ color: 'rgba(255,255,255,0.7)' }}>
              View-Only — Project Status Overview
            </Text>
          </div>
          <Select
            value={selectedProject}
            onChange={handleProjectChange}
            style={{ width: 280 }}
            options={projects.map(p => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
          />
        </div>

        {projectDetails && (
          <div style={{ marginTop: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Tag color="blue" style={{ fontSize: 13, padding: '4px 12px' }}>
              {projectDetails.code}
            </Tag>
            <Tag color="green" style={{ fontSize: 13, padding: '4px 12px' }}>
              {projectDetails.status}
            </Tag>
            <Tag color="red" style={{ fontSize: 13, padding: '4px 12px' }}>
              {projectDetails.criticality}
            </Tag>
            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>
              Client: {projectDetails.client_name}
            </Text>
          </div>
        )}
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}

      {data && (
        <>
          {/* Quality Status */}
          <Title level={5} style={{ color: '#1B2A4A', marginBottom: 16 }}>
            Quality Status
          </Title>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card style={{ borderRadius: 12, borderLeft: '4px solid #ff4d4f', textAlign: 'center' }}>
                <SafetyOutlined style={{ fontSize: 32, color: '#ff4d4f', marginBottom: 8 }} />
                <Statistic
                  title="Critical NCRs Open"
                  value={data.ncr_summary.critical}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card style={{ borderRadius: 12, borderLeft: '4px solid #faad14', textAlign: 'center' }}>
                <ExclamationCircleOutlined style={{ fontSize: 32, color: '#faad14', marginBottom: 8 }} />
                <Statistic
                  title="Major NCRs Open"
                  value={data.ncr_summary.major}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card style={{ borderRadius: 12, borderLeft: '4px solid #52c41a', textAlign: 'center' }}>
                <CheckCircleOutlined style={{ fontSize: 32, color: '#52c41a', marginBottom: 8 }} />
                <Statistic
                  title="Minor NCRs Open"
                  value={data.ncr_summary.minor}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card style={{ borderRadius: 12, borderLeft: '4px solid #1890ff', textAlign: 'center' }}>
                <BarChartOutlined style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }} />
                <Statistic
                  title="Total Open NCRs"
                  value={data.ncr_summary.total_open}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Progress Status */}
          <Title level={5} style={{ color: '#1B2A4A', marginBottom: 16 }}>
            Today's Progress
          </Title>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card style={{ borderRadius: 12 }}>
                <Title level={5} style={{ color: '#1B2A4A' }}>Daily Progress Report</Title>
                {data.dpr_today.submitted ? (
                  <div>
                    <Tag icon={<CheckCircleOutlined />} color="green" style={{ marginBottom: 16 }}>
                      DPR Submitted Today
                    </Tag>
                    <div style={{ marginBottom: 8 }}>
                      <Text type="secondary">Overall Progress</Text>
                    </div>
                    <Progress
                      percent={parseFloat(data.dpr_today.actual_progress_pct) || 0}
                      strokeColor="#2E75B6"
                      format={p => `${p}%`}
                    />
                    <div style={{ marginTop: 16 }}>
                      <Text type="secondary">Manpower on Site: </Text>
                      <Text strong>{data.dpr_today.manpower_count}</Text>
                    </div>
                  </div>
                ) : (
                  <Tag icon={<ExclamationCircleOutlined />} color="orange">
                    DPR Not Yet Submitted Today
                  </Tag>
                )}
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card style={{ borderRadius: 12 }}>
                <Title level={5} style={{ color: '#1B2A4A' }}>Action Items Summary</Title>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Statistic
                      title="Pending MOM Actions"
                      value={data.pending_mom_actions}
                      valueStyle={{ color: '#faad14' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Overdue Items"
                      value={data.overdue_items}
                      valueStyle={{ color: data.overdue_items > 0 ? '#ff4d4f' : '#52c41a' }}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          <div style={{ marginTop: 24, textAlign: 'right' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Data as of: {new Date(data.as_of).toLocaleString('en-IN')} | View-Only Access
            </Text>
          </div>
        </>
      )}
    </div>
  );
};

export default ClientDashboardPage;